import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-theme-demo',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    CardModule,
    InputTextModule
  ],
  template: `
    <div class="p-4">
      <div class="max-w-6xl mx-auto">
        
        <!-- Header -->
        <div class="text-center mb-8">
          <h1 class="text-display-md mb-4">Mosaic Dark Theme Demo</h1>
          <p class="text-body-lg text-white-muted">Showcasing PrimeNG components with modern typography and gradient styling</p>
        </div>

        <!-- Button Showcase -->
        <p-card header="Button Variations" class="mb-8">
          <div class="flex flex-wrap gap-4 items-center">
            <p-button label="Primary Button" />
            <p-button label="Secondary" severity="secondary" />
            <p-button label="Outlined" [outlined]="true" />
            <p-button label="Text Button" [text]="true" />
            <p-button label="Disabled" [disabled]="true" />
            <p-button label="With Icon" icon="pi pi-star" />
          </div>
        </p-card>

        <!-- Form Components -->
        <p-card header="Form Components" class="mb-8">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div class="flex flex-col gap-2">
              <label>Input Text</label>
              <input 
                pInputText 
                placeholder="Enter text here..." 
                [(ngModel)]="inputValue"
                class="w-full" />
            </div>
            
            <div class="flex flex-col gap-2">
              <label>Another Input</label>
              <input 
                pInputText 
                placeholder="Another input..."
                [(ngModel)]="inputValue2"
                class="w-full" />
            </div>
          </div>
        </p-card>

        <!-- Gradient Showcase -->
        <p-card header="Custom Gradient Showcase" class="mb-8">
          <div class="space-y-6">
            <div>
              <h3 class="text-heading-lg mb-4">Primary Gradient Background</h3>
              <p class="text-body-md text-white-muted mb-4">This demonstrates the custom gradient background: linear-gradient(90deg, #806EDD 0%, #5C4AB8 100%)</p>
              <div class="bg-gradient-primary p-6 rounded-lg shadow-primary">
                <h4 class="text-heading-md m-0 text-white">Gradient Background Example</h4>
                <p class="text-body-md mt-2 mb-0 text-white-secondary">Clean white text on gradient background with proper contrast</p>
              </div>
            </div>
            
            <div>
              <h4 class="text-heading-md mb-4">Additional Gradient Examples</h4>
              <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div class="bg-gradient-primary-vertical p-4 rounded-lg text-white text-center">
                  <span class="text-label-lg font-semibold">Vertical Gradient</span>
                </div>
                <div class="bg-gradient-primary-diagonal p-4 rounded-lg text-white text-center">
                  <span class="text-label-lg font-semibold">Diagonal Gradient</span>
                </div>
                <div class="bg-primary-600 p-4 rounded-lg text-white text-center">
                  <span class="text-label-lg font-semibold">Solid Primary</span>
                </div>
              </div>
            </div>
            
            <div>
              <h4 class="text-heading-md mb-4">Button Examples</h4>
              <div class="flex flex-wrap gap-4">
                <p-button label="Standard Primary" />
                <p-button label="Secondary Style" severity="secondary" />
                <p-button label="Outlined Style" [outlined]="true" />
              </div>
            </div>
          </div>
        </p-card>

        <!-- Color Palette Demo -->
        <p-card header="Enhanced Color Palette" class="mb-8">
          <div class="space-y-6">
            <!-- Primary Color Scale -->
            <div>
              <h4 class="text-heading-md mb-4">Primary Color Scale</h4>
              <div class="flex flex-wrap gap-2">
                <div class="bg-primary-50 text-primary-900 p-3 rounded text-center min-w-20 text-caption-lg font-medium">50</div>
                <div class="bg-primary-100 text-primary-900 p-3 rounded text-center min-w-20 text-caption-lg font-medium">100</div>
                <div class="bg-primary-200 text-primary-900 p-3 rounded text-center min-w-20 text-caption-lg font-medium">200</div>
                <div class="bg-primary-300 text-white p-3 rounded text-center min-w-20 text-caption-lg font-medium">300</div>
                <div class="bg-primary-400 text-white p-3 rounded text-center min-w-20 text-caption-lg font-medium">400</div>
                <div class="bg-primary-500 text-white p-3 rounded text-center min-w-20 text-caption-lg font-medium">500</div>
                <div class="bg-primary-600 text-white p-3 rounded text-center min-w-20 text-caption-lg font-medium">600</div>
                <div class="bg-primary-700 text-white p-3 rounded text-center min-w-20 text-caption-lg font-medium">700</div>
                <div class="bg-primary-800 text-white p-3 rounded text-center min-w-20 text-caption-lg font-medium">800</div>
                <div class="bg-primary-900 text-white p-3 rounded text-center min-w-20 text-caption-lg font-medium">900</div>
              </div>
            </div>
            
            <!-- Gradient Variations -->
            <div>
              <h4 class="text-heading-md mb-4">Gradient Variations</h4>
              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div class="bg-gradient-primary p-6 rounded-lg text-white text-center text-label-lg font-semibold shadow-primary">
                  Primary Gradient
                </div>
                <div class="bg-gradient-primary-vertical p-6 rounded-lg text-white text-center text-label-lg font-semibold shadow-primary">
                  Vertical
                </div>
                <div class="bg-gradient-primary-diagonal p-6 rounded-lg text-white text-center text-label-lg font-semibold shadow-primary">
                  Diagonal
                </div>
                <div class="bg-gradient-primary p-6 rounded-lg text-white text-center text-label-lg font-semibold shadow-primary-lg">
                  With Glow
                </div>
              </div>
            </div>
          </div>
        </p-card>

        <!-- Interactive Elements Demo -->
        <p-card header="Interactive Elements with Tailwind Utilities" class="mb-8">
          <div class="space-y-6">
            <!-- Custom styled elements using new Tailwind utilities -->
            <div>
              <h4 class="text-heading-md mb-4">Custom Button Classes</h4>
              <div class="flex flex-wrap gap-4">
                <!-- New Tailwind component classes -->
                <button class="btn-gradient">
                  Gradient Button
                </button>
                
                <button class="btn-outline-primary">
                  Outlined Primary
                </button>
                
                <button class="btn-ghost">
                  Ghost Button
                </button>
                
                <!-- Utility combinations -->
                <button class="bg-primary-600 px-6 py-3 rounded-lg text-white text-label-md font-semibold">
                  Button
                </button>
              </div>
            </div>
            
            <!-- Glass effect cards -->
            <div>
              <h4 class="text-heading-md mb-4">Glass Effect Cards</h4>
              <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div class="card-glass">
                  <h5 class="text-heading-sm text-white mb-2">Glass Card</h5>
                  <p class="text-white-secondary text-body-sm">Uses backdrop blur effect</p>
                </div>
                
                <div class="card-surface">
                  <h5 class="text-heading-sm text-white mb-2">Surface Card</h5>
                  <p class="text-white-secondary text-body-sm">Uses surface colors</p>
                </div>
                
                <div class="card-gradient">
                  <h5 class="text-heading-sm text-white mb-2">Gradient Card</h5>
                  <p class="text-white-muted text-body-sm">Full gradient background</p>
                </div>
              </div>
            </div>
            
            <!-- Text utilities -->
            <div>
              <h4 class="text-heading-md mb-4">Modern Typography Showcase</h4>
              <div class="space-y-4">
                <div>
                  <h5 class="text-heading-sm mb-2">Display Typography</h5>
                  <p class="text-display-sm">Display Large Text</p>
                  <p class="text-display-md">Display Medium Text</p>
                </div>
                <div>
                  <h5 class="text-heading-sm mb-2">Heading Typography</h5>
                  <p class="text-heading-xl">Heading XL</p>
                  <p class="text-heading-lg">Heading Large</p>
                  <p class="text-heading-md">Heading Medium</p>
                  <p class="text-heading-sm">Heading Small</p>
                </div>
                <div>
                  <h5 class="text-heading-sm mb-2">Body Typography</h5>
                  <p class="text-body-lg">Body Large - Perfect for important content</p>
                  <p class="text-body-md">Body Medium - Standard body text</p>
                  <p class="text-body-sm">Body Small - For secondary information</p>
                </div>
                <div>
                  <h5 class="text-heading-sm mb-2">Semantic Colors</h5>
                  <p class="text-white-primary">Primary white text (100% opacity)</p>
                  <p class="text-white-secondary">Secondary white text (90% opacity)</p>
                  <p class="text-white-muted">Muted white text (70% opacity)</p>
                  <p class="text-primary">Primary colored text</p>
                  <p class="text-success">Success text color</p>
                  <p class="text-warning">Warning text color</p>
                  <p class="text-error">Error text color</p>
                </div>
              </div>
            </div>
            
            <!-- Interactive demos -->
            <div>
              <h4 class="text-heading-md mb-4">Interactive Examples</h4>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <!-- Glass card with hover effect -->
                <div class="glass hover-lift cursor-pointer p-4 rounded-lg">
                  <h5 class="text-heading-sm text-white mb-2">Hoverable Glass</h5>
                  <p class="text-white-secondary text-body-sm">Hover for lift effect</p>
                </div>
                
                <!-- Custom input styling -->
                <div>
                  <label class="block text-white-primary text-label-md font-medium mb-2">Custom Input</label>
                  <input 
                    class="input-dark w-full"
                    placeholder="Uses custom input styling..."
                    [(ngModel)]="customInput" />
                </div>
              </div>
            </div>
          </div>
        </p-card>

      </div>
    </div>
  `
})
export class ThemeDemoComponent {
  inputValue = '';
  inputValue2 = '';
  customInput = '';
}